import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  MoreHorizontal,
  Store,
  Loader2,
  Pencil,
  Trash2,
  Star,
  Upload,
  Download,
  Eye,
  LayoutGrid,
  List,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
} from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ProductModal } from '@/components/trading/product-modal';
import { ProductImportModal } from '@/components/trading/product-import-modal';
import { ProductDiscountDialog } from '@/components/trading/product-discount-dialog';
import {
  PriceListExportDialog,
  type PriceListExportOptions,
} from '@/components/trading/price-list-export-dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast, showWarningToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { dateStamp } from '@/lib/download-xlsx';
import { buildPriceListDoc } from '@/lib/price-list-doc';
import { exportTableReportXlsx } from '@/lib/report-table-xlsx';
import { downloadTableReportPdf } from '@/lib/report-table-pdf';
import { downscaleAll } from '@/lib/image-thumbnail';

type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'PRE_ORDER' | 'DISCONTINUED';
type ViewMode = 'grid' | 'table';
type SortKey = 'code' | 'name' | 'brand' | 'model' | 'price';
type SortDir = 'asc' | 'desc';

interface TradingProduct {
  id: string;
  modelId: string;
  code: string;
  fullCode: string;
  sku?: string | null;
  name: string;
  slug: string;
  shortDescription?: string | null;
  description?: string | null;
  specifications?: unknown;
  price?: number | null;
  currency: string;
  priceOnRequest: boolean;
  stockStatus: StockStatus;
  minOrderQty: number;
  isActive: boolean;
  isFeatured: boolean;
  sortOrder: number;
  model: {
    id: string;
    code: string;
    name: string;
    slug: string;
    brand: {
      id: string;
      code: string;
      name: string;
      slug: string;
    };
  };
  images: Array<{
    id: string;
    url: string;
    isPrimary: boolean;
    createdAt?: Date;
    alt?: string | null;
  }>;
  _count?: {
    inquiryItems: number;
  };
}

const stockStatusColors: Record<StockStatus, string> = {
  IN_STOCK: 'bg-green-100 text-green-700',
  LOW_STOCK: 'bg-yellow-100 text-yellow-700',
  OUT_OF_STOCK: 'bg-red-100 text-red-700',
  PRE_ORDER: 'bg-blue-100 text-blue-700',
  DISCONTINUED: 'bg-gray-100 text-gray-700',
};

const PAGE_SIZE = 50;
// Selectable fetch sizes — a larger page means the whole catalogue loads in one
// (or few) requests, so most users never need infinite scroll at all.
const PAGE_SIZE_OPTIONS = [50, 100, 150, 250] as const;
// Card layout: a 4:3 image (≈ 150 px tall at 200 px wide) plus the meta block.
// This is only the INITIAL estimate — the window virtualiser re-measures each
// row from the DOM (via measureElement), so a wrong estimate self-corrects
// instead of leaving a tall empty gap before the load-more sentinel.
const GRID_CARD_HEIGHT = 280;
const GRID_GAP = 12; // matches gap-3
const TABLE_ROW_HEIGHT = 56;

// Grid column count derived from viewport width. More aggressive breakpoints
// than the old md/lg/xl so accessory cards aren't oversized on big screens.
function useGridColumns(): number {
  const [cols, setCols] = useState(() => columnsForWidth(window.innerWidth));
  useEffect(() => {
    const update = () => setCols(columnsForWidth(window.innerWidth));
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return cols;
}
function columnsForWidth(w: number): number {
  if (w >= 1920) return 6;
  if (w >= 1536) return 5;
  if (w >= 1280) return 4;
  if (w >= 1024) return 3;
  if (w >= 640) return 2;
  return 1;
}

export function TradingProductsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canCreate = usePermission('PRODUCTS', 'CREATE');
  const canEdit = usePermission('PRODUCTS', 'EDIT');
  const canDelete = usePermission('PRODUCTS', 'DELETE');
  // Filters/sort/view persist per-tab (sessionStorage) so opening a product and
  // coming back keeps the list exactly as it was. Stable filters also keep the
  // same React Query key, so the already-loaded pages return from cache.
  const [debouncedSearch, setDebouncedSearch] = useSessionState<string>(
    'trading:products:search',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [brandFilter, setBrandFilter] = useSessionState<string | undefined>(
    'trading:products:brand',
    undefined
  );
  const [modelFilter, setModelFilter] = useSessionState<string | undefined>(
    'trading:products:model',
    undefined
  );
  const [stockFilter, setStockFilter] = useSessionState<StockStatus | undefined>(
    'trading:products:stock',
    undefined
  );
  const [viewMode, setViewMode] = useSessionState<ViewMode>('trading:products:view', 'grid');
  const [sortBy, setSortBy] = useSessionState<SortKey | undefined>(
    'trading:products:sortBy',
    undefined
  );
  const [sortDir, setSortDir] = useSessionState<SortDir>('trading:products:sortDir', 'asc');
  const [pageSize, setPageSize] = useSessionState<number>('trading:products:pageSize', PAGE_SIZE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingProduct, setEditingProduct] = useState<TradingProduct | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => setDebouncedSearch(value), 300);
  };

  // Click a column header to sort by it; click again to flip direction.
  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  // Get brands for filter, ordered by code to match the "code - name" labels
  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  const brands = useMemo(
    () =>
      [...(brandsData?.brands ?? [])].sort((a, b) =>
        a.code.localeCompare(b.code, undefined, { numeric: true })
      ),
    [brandsData]
  );

  // Get models for selected brand
  const { data: modelsData } = trpc.tradingModel.list.useQuery(
    { brandId: brandFilter },
    { enabled: !!brandFilter }
  );
  const models = modelsData?.models ?? [];

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.tradingProduct.list.useInfiniteQuery(
      {
        limit: pageSize,
        search: debouncedSearch || undefined,
        brandId: brandFilter,
        modelId: modelFilter,
        stockStatus: stockFilter,
        sortBy,
        sortDir,
      },
      {
        initialCursor: 1,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      }
    );

  const products = useMemo<TradingProduct[]>(
    () => (data?.pages.flatMap((p) => p.products) ?? []) as TradingProduct[],
    [data]
  );
  const totalRowCount = data?.pages[0]?.pagination.total ?? products.length;

  const utils = trpc.useUtils();

  const deleteMutation = trpc.tradingProduct.delete.useMutation({
    onSuccess: () => {
      utils.tradingProduct.list.invalidate();
      utils.tradingModel.list.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
    },
    onError: (err) => showErrorToast(err.message),
  });

  const handleAddProduct = () => {
    setEditingProduct(undefined);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Omit<TradingProduct, 'price'> & { price?: unknown }) => {
    const price =
      product.price != null
        ? typeof product.price === 'object' && 'toNumber' in product.price
          ? (product.price as { toNumber: () => number }).toNumber()
          : Number(product.price)
        : null;
    setEditingProduct({ ...product, price } as TradingProduct);
    setIsModalOpen(true);
  };

  const handleDeleteProduct = (id: string) => {
    setProductToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (productToDelete) deleteMutation.mutate(productToDelete);
  };

  const runExport = async (o: PriceListExportOptions) => {
    setExporting(true);
    try {
      const data = await utils.tradingProduct.exportPriceList.fetch({
        search: debouncedSearch || undefined,
        brandId: brandFilter,
        modelId: modelFilter,
        stockStatus: stockFilter,
        includeImages: o.includeImages,
      });

      // Base-currency prices are frozen (purchase-date cost basis) and never
      // depend on today's rate — a stale live rate only affects the forced
      // display-currency columns, so warn only when those were requested.
      if (data.fxStale && o.currencies.length > 0) {
        const rateDate = new Date(data.fxDate).toLocaleDateString('tr-TR');
        showWarningToast(
          t('products.exportFxStale', {
            date: rateDate,
            defaultValue:
              'Canlı kur alınamadı — çevrim sütunları {{date}} tarihli (son kayıtlı) kurla hesaplandı. Ürün para birimindeki fiyatlar etkilenmez.',
          })
        );
      }

      // Inline thumbnails (PDF only): the server returns full-res data URLs
      // (fetched server-side, no CORS); downscale them here to keep the PDF
      // small. data: URLs are canvas-safe so this never hits a CORS wall.
      let exportProducts = data.products;
      let imagesIncluded = data.imagesIncluded;
      if (o.includeImages && o.format === 'pdf') {
        const thumbs = await downscaleAll(exportProducts.map((p) => p.imageUrl));
        exportProducts = exportProducts.map((p, i) => ({ ...p, imageUrl: thumbs[i] ?? null }));
        // Count what will actually render (a downscale can fail independently).
        imagesIncluded = thumbs.filter(Boolean).length;
      }

      const doc = buildPriceListDoc(exportProducts, {
        mode: o.mode,
        includeCost: o.includeCost,
        currencies: o.currencies,
        withKdv: o.withKdv,
        priceType: o.priceType,
        fxRates: data.fxRates,
        fxDate: data.fxDate,
        fxSource: data.fxSource,
        fxStale: data.fxStale,
        includeImages: o.includeImages && o.format === 'pdf',
        imagesIncluded,
        imagesTotal: data.imagesTotal,
        visibleColumns: o.columns,
      });
      const fn = `trading-fiyat-listesi-${o.priceType === 'vendor' ? 'bayi' : 'perakende'}-${
        o.mode === 'lot' ? 'parti' : 'urun'
      }-${dateStamp()}.${o.format === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (o.format === 'pdf') await downloadTableReportPdf(doc, fn);
      else await exportTableReportXlsx(doc, fn);
      setExportOpen(false);
    } catch (e) {
      showErrorToast(
        e instanceof Error ? e.message : t('products.exportFailed', 'Dışa aktarma başarısız')
      );
    } finally {
      setExporting(false);
    }
  };

  const getStockStatusKey = (status: StockStatus): string => {
    const map: Record<StockStatus, string> = {
      IN_STOCK: 'inStock',
      LOW_STOCK: 'lowStock',
      OUT_OF_STOCK: 'outOfStock',
      PRE_ORDER: 'preOrder',
      DISCONTINUED: 'discontinued',
    };
    return map[status];
  };

  // ── Virtualisation ───────────────────────────────────────────────────────
  // The admin page does NOT scroll the window — the scroll container is the
  // layout's <main className="overflow-y-auto"> (the root is h-screen /
  // overflow-hidden). So the virtualiser must be bound to that element, not the
  // window; otherwise its scroll offset stays 0, only the first window of rows
  // ever renders, and the user sees one page then an enormous blank gap.
  const cols = useGridColumns();
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const [listOffset, setListOffset] = useState(0);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [filtersHeight, setFiltersHeight] = useState(48);
  // The scroll container (<main>) has top padding, so a plain `sticky top-0`
  // bar docks *below* that padding and lets virtualized rows bleed through the
  // gap above it. Measure the padding and dock with a negative top so the bar
  // sits flush against the scroll container's visible top edge.
  const [stickyTop, setStickyTop] = useState(0);

  // Resolve the real scroll container (<main>) once mounted, and re-measure the
  // sticky filter bar height + the container's top padding on resize. Resolve
  // from the filter bar (always rendered) — NOT the list, which only mounts once
  // products have loaded; otherwise scrollEl would latch to null and the
  // virtualiser would never get a scroll element (blank list despite a count).
  useLayoutEffect(() => {
    const barEl = filtersRef.current;
    const el = (barEl?.closest('main') as HTMLElement | null) ?? null;
    setScrollEl(el);
    const measure = () => {
      if (barEl) setFiltersHeight(barEl.offsetHeight);
      if (el) setStickyTop(-parseFloat(getComputedStyle(el).paddingTop || '0'));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (barEl) ro.observe(barEl);
    if (el) ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // scrollMargin = the list's offset within the scroll container's content.
  // Computed from bounding rects (+ current scrollTop) so it's robust to the
  // offsetParent chain rather than assuming the list's offsetParent is <main>.
  useLayoutEffect(() => {
    if (!listRef.current || !scrollEl) return;
    const margin =
      listRef.current.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top +
      scrollEl.scrollTop;
    setListOffset(margin);
  }, [scrollEl, viewMode, debouncedSearch, brandFilter, modelFilter, stockFilter, sortBy, sortDir, pageSize, products.length]);

  const rowCount = viewMode === 'grid' ? Math.ceil(products.length / cols) : products.length;

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollEl,
    estimateSize: () => (viewMode === 'grid' ? GRID_CARD_HEIGHT + GRID_GAP : TABLE_ROW_HEIGHT),
    overscan: viewMode === 'grid' ? 3 : 10,
    scrollMargin: listOffset,
  });

  // Discard stale per-index measurements when the layout mode changes: a single
  // virtualizer serves both views (and the grid's column count), so without this
  // the table would briefly reuse grid row heights (and vice-versa), flashing a
  // gap/overlap until each row re-measures.
  useLayoutEffect(() => {
    rowVirtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, cols]);

  // Reset scroll when the user changes a filter/view — but NOT on the initial
  // mount, where we instead restore the previous scroll position.
  const didRestoreScroll = useRef(false);
  useEffect(() => {
    if (!didRestoreScroll.current) return;
    scrollEl?.scrollTo({ top: 0 });
  }, [scrollEl, viewMode, debouncedSearch, brandFilter, modelFilter, stockFilter, sortBy, sortDir, pageSize]);

  // Persist the scroll position (one write per frame) so we can return the user
  // to where they were after they open a product and come back.
  useEffect(() => {
    if (!scrollEl) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        try {
          sessionStorage.setItem('trading:products:scrollY', String(scrollEl.scrollTop));
        } catch {
          /* best-effort */
        }
        raf = 0;
      });
    };
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollEl]);

  // Restore the saved scroll position once the scroll container is available.
  // The rows come back from the React Query cache, so the page already has ~its
  // prior height; re-apply over a few frames while the virtualizer finishes
  // measuring, then re-enable the reset-to-top behaviour above.
  useEffect(() => {
    if (!scrollEl) return;
    const saved = Number(sessionStorage.getItem('trading:products:scrollY') || 0);
    if (saved <= 0) {
      didRestoreScroll.current = true;
      return;
    }
    let tries = 0;
    const apply = () => {
      scrollEl.scrollTo({ top: saved });
      tries += 1;
      if (tries < 8 && Math.abs(scrollEl.scrollTop - saved) > 2) {
        requestAnimationFrame(apply);
      } else {
        didRestoreScroll.current = true;
      }
    };
    requestAnimationFrame(apply);
  }, [scrollEl]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  // Infinite scroll — IntersectionObserver on a sentinel below the virtualised
  // area, rooted at the real scroll container so the lookahead is measured
  // against the element that actually scrolls.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current || !scrollEl) return;
    const node = sentinelRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      // 600 px lookahead so we start the next page before the user hits the
      // very last row.
      { root: scrollEl, rootMargin: '600px 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [scrollEl, hasNextPage, isFetchingNextPage, fetchNextPage, products.length]);

  // Sortable column header — a real <button> so it is keyboard-operable and
  // screen-reader announced; the chevron + aria-label convey current sort state.
  const renderSortHeader = (labelKey: string, key: SortKey, alignRight = false) => {
    const label = t(`products.${labelKey}`);
    const active = sortBy === key;
    const ariaLabel = active
      ? t(sortDir === 'asc' ? 'products.sortedAscending' : 'products.sortedDescending', {
          column: label,
        })
      : t('products.sortBy', { column: label });
    return (
      <button
        type="button"
        onClick={() => toggleSort(key)}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={`hover:text-foreground focus-visible:ring-foreground/50 flex items-center gap-1 px-3 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
          alignRight ? 'justify-end' : ''
        } ${active ? 'text-foreground' : ''}`}
      >
        <span className="truncate">{label}</span>
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3 w-3 shrink-0" />
          ) : (
            <ArrowDown className="h-3 w-3 shrink-0" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-40" />
        )}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{t('products.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('products.subtitle')}
            {totalRowCount > 0 && ` (${totalRowCount} ${tCommon('total')})`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && <Button variant="outline" className="h-8" onClick={() => setDiscountOpen(true)}>{t('products.bulkDiscount.title')}</Button>}
          <Button variant="outline" className="h-8" onClick={() => setExportOpen(true)}>
            <Download className="mr-2 h-4 w-4" />
            {t('products.export', 'Dışa Aktar')}
          </Button>
          {canCreate && (
            <>
              <Button variant="outline" className="h-8" onClick={() => setIsImportModalOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                {t('products.importProducts')}
              </Button>
              <Button className="h-8" onClick={handleAddProduct}>
                <Plus className="mr-2 h-4 w-4" />
                {t('products.addProduct')}
              </Button>
            </>
          )}
        </div>
      </div>

      <ProductModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        product={editingProduct}
        defaultModelId={modelFilter}
      />

      <ProductImportModal open={isImportModalOpen} onOpenChange={setIsImportModalOpen} />
      <ProductDiscountDialog open={discountOpen} onOpenChange={setDiscountOpen} />

      <PriceListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        exporting={exporting}
        onExport={runExport}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('products.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Filters + View Toggle — sticky so they stay visible while scrolling */}
      <div
        ref={filtersRef}
        style={{ top: stickyTop }}
        className="bg-background border-border-strong sticky z-20 flex flex-wrap items-center gap-3 border-b py-2 shadow-sm"
      >
        <div className="relative max-w-xs flex-1">
          <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('products.searchPlaceholder')}
            aria-label={t('products.searchAriaLabel')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-8 pr-3 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={brandFilter ?? ''}
          aria-label={t('products.filterByBrand')}
          onChange={(e) => {
            setBrandFilter(e.target.value || undefined);
            setModelFilter(undefined);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('products.allBrands')}</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.code} - {brand.name}
            </option>
          ))}
        </select>
        {brandFilter && (
          <select
            value={modelFilter ?? ''}
            aria-label={t('products.filterByModel')}
            onChange={(e) => setModelFilter(e.target.value || undefined)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2 text-sm focus:outline-none focus:ring-1"
          >
            <option value="">{t('products.allModels')}</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.code} - {model.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={stockFilter ?? ''}
          aria-label={t('products.filterByStatus')}
          onChange={(e) => setStockFilter((e.target.value as StockStatus) || undefined)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('products.allStatuses')}</option>
          <option value="IN_STOCK">{t('products.inStock')}</option>
          <option value="LOW_STOCK">{t('products.lowStock')}</option>
          <option value="OUT_OF_STOCK">{t('products.outOfStock')}</option>
          <option value="PRE_ORDER">{t('products.preOrder')}</option>
          <option value="DISCONTINUED">{t('products.discontinued')}</option>
        </select>

        <select
          value={pageSize}
          aria-label={t('products.pageSize', 'Sayfa boyutu')}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2 text-sm focus:outline-none focus:ring-1"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n} {t('products.perPageSuffix', '/ sayfa')}
            </option>
          ))}
        </select>

        {!isLoading && !error && products.length > 0 && (
          <span className="text-muted-foreground text-xs tabular-nums">
            {products.length} / {totalRowCount}
          </span>
        )}

        <div className="border-border ml-auto flex border">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            aria-label={t('products.gridView')}
            aria-pressed={viewMode === 'grid'}
            className={`focus-visible:ring-foreground/50 p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${viewMode === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            aria-label={t('products.tableView')}
            aria-pressed={viewMode === 'table'}
            className={`focus-visible:ring-foreground/50 p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${viewMode === 'table' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          <span className="text-muted-foreground ml-2 text-sm">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p>{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Store className="text-muted-foreground/50 h-10 w-10" />
          <h3 className="mt-3 text-sm font-medium">{t('products.noProducts')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {debouncedSearch || brandFilter || modelFilter || stockFilter
              ? t('products.noProducts')
              : t('products.noProductsDesc')}
          </p>
          {!debouncedSearch && !brandFilter && !modelFilter && !stockFilter && canCreate && (
            <Button className="mt-4 h-8" onClick={handleAddProduct}>
              <Plus className="mr-2 h-4 w-4" />
              {t('products.addProduct')}
            </Button>
          )}
        </div>
      )}

      {/* Window-scrolled virtual list — no nested scroll container. The whole
          admin page scrolls and the virtualizer measures rows against the
          window viewport using listRef.offsetTop. */}
      {!isLoading && !error && products.length > 0 && (
        <div ref={listRef} className="relative">
          {viewMode === 'table' && (
            <div
              role="row"
              className="bg-muted/95 text-muted-foreground border-border border-b-border-strong sticky z-[5] grid border-b-2 border-t text-xs font-medium uppercase tracking-wide backdrop-blur"
              style={{
                top: filtersHeight + stickyTop,
                gridTemplateColumns: '52px 140px minmax(0, 1fr) 120px 120px 120px 140px 48px',
              }}
            >
              <div className="px-3 py-2" />
              {renderSortHeader('code', 'code')}
              {renderSortHeader('name', 'name')}
              {renderSortHeader('brand', 'brand')}
              {renderSortHeader('model', 'model')}
              <div className="px-3 py-2">{t('products.stockStatus')}</div>
              {renderSortHeader('price', 'price', true)}
              <div className="px-3 py-2 text-center" />
            </div>
          )}

          <div
            style={{
              height: rowVirtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualRow) => {
              // Window virtualiser reports absolute scroll positions; subtract
              // the list's offset from page top so the translateY is relative
              // to our container, not the document.
              const top = virtualRow.start - rowVirtualizer.options.scrollMargin;

              if (viewMode === 'grid') {
                const startIdx = virtualRow.index * cols;
                const rowItems = products.slice(startIdx, startIdx + cols);
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className="absolute left-0 grid w-full"
                    style={{
                      top: 0,
                      transform: `translateY(${top}px)`,
                      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                      gap: GRID_GAP,
                      paddingBottom: GRID_GAP,
                    }}
                  >
                    {rowItems.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        stockStatusLabel={t(`products.${getStockStatusKey(product.stockStatus)}`)}
                        stockStatusClass={stockStatusColors[product.stockStatus]}
                        priceOnRequestLabel={t('products.priceOnRequest')}
                        onClick={() => navigate(`/trading/products/${product.id}`)}
                        onView={() => navigate(`/trading/products/${product.id}`)}
                        onEdit={canEdit ? () => handleEditProduct(product) : undefined}
                        onDelete={canDelete ? () => handleDeleteProduct(product.id) : undefined}
                        tCommon={tCommon}
                      />
                    ))}
                  </div>
                );
              }

              const product = products[virtualRow.index]!;
              const primaryImage = product.images.find((img) => img.isPrimary) || product.images[0];
              return (
                <div
                  key={product.id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  role="row"
                  className="border-border hover:bg-muted/30 [&:has(a:focus-visible)]:outline-foreground/40 absolute left-0 grid w-full items-center border-b text-sm [&:has(a:focus-visible)]:outline [&:has(a:focus-visible)]:outline-2 [&:has(a:focus-visible)]:-outline-offset-2"
                  style={{
                    top: 0,
                    transform: `translateY(${top}px)`,
                    gridTemplateColumns: '52px 140px minmax(0, 1fr) 120px 120px 120px 140px 48px',
                    minHeight: TABLE_ROW_HEIGHT,
                  }}
                >
                  <Link
                    to={`/trading/products/${product.id}`}
                    aria-label={product.name}
                    className="text-foreground contents cursor-pointer no-underline"
                  >
                    <div className="px-3 py-2">
                      <div className="bg-muted/50 flex h-9 w-9 items-center justify-center overflow-hidden rounded">
                        {primaryImage ? (
                          <img
                            src={primaryImage.url}
                            alt={product.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <Store className="text-muted-foreground h-4 w-4" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 px-3 py-2">
                      <span className="font-mono text-xs">{product.fullCode}</span>
                      {product.isFeatured && (
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      )}
                    </div>
                    <div className="truncate px-3 py-2 font-medium">{product.name}</div>
                    <div className="text-muted-foreground truncate px-3 py-2 text-xs">
                      {product.model.brand.name}
                    </div>
                    <div className="text-muted-foreground truncate px-3 py-2 text-xs">
                      {product.model.name}
                    </div>
                    <div className="px-3 py-2">
                      <span
                        className={`px-1.5 py-0.5 text-[10px] font-medium ${stockStatusColors[product.stockStatus]}`}
                      >
                        {t(`products.${getStockStatusKey(product.stockStatus)}`)}
                      </span>
                    </div>
                    <div className="px-3 py-2 text-right text-xs tabular-nums">
                      {product.priceOnRequest ? (
                        <span className="text-muted-foreground">
                          {t('products.priceOnRequest')}
                        </span>
                      ) : product.price ? (
                        <span className="font-medium">
                          {product.currency} {Number(product.price).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </Link>
                  <div className="px-3 py-2 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={tCommon('actions')}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => navigate(`/trading/products/${product.id}`)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          {tCommon('view')}
                        </DropdownMenuItem>
                        {canEdit && (
                          <DropdownMenuItem onClick={() => handleEditProduct(product)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {tCommon('edit')}
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {tCommon('delete')}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>

          {/* IntersectionObserver target — fires fetchNextPage when scrolled
              within 600 px of the bottom of the virtual list. */}
          <div ref={sentinelRef} aria-hidden className="h-1 w-full" />

          {isFetchingNextPage && (
            <div className="text-muted-foreground flex items-center justify-center py-4 text-sm">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          )}
          {!hasNextPage && products.length >= pageSize && (
            <div className="text-muted-foreground py-3 text-center text-xs">
              {tCommon('endOfList', { defaultValue: 'End of list' })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Grid card extracted so the virtualised row stays compact ────────────────
interface ProductCardProps {
  product: TradingProduct;
  stockStatusLabel: string;
  stockStatusClass: string;
  priceOnRequestLabel: string;
  onClick: () => void;
  onView: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  tCommon: (k: string) => string;
}
function ProductCard({
  product,
  stockStatusLabel,
  stockStatusClass,
  priceOnRequestLabel,
  onClick,
  onView,
  onEdit,
  onDelete,
  tCommon,
}: ProductCardProps) {
  const primaryImage = product.images.find((img) => img.isPrimary) || product.images[0];
  return (
    <div
      className="border-border bg-card hover:border-foreground/40 group flex cursor-pointer flex-col overflow-hidden rounded-md border transition-all hover:shadow-sm"
      onClick={onClick}
    >
      {/* 4:3 image — keeps the card compact so meta + price always fit */}
      <div className="bg-muted/40 relative aspect-[4/3] overflow-hidden">
        {primaryImage ? (
          <img
            src={primaryImage.url}
            alt={product.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Store className="text-muted-foreground/40 h-10 w-10" />
          </div>
        )}
        {product.isFeatured && (
          <Star className="absolute left-1.5 top-1.5 h-4 w-4 fill-yellow-400 text-yellow-400 drop-shadow" />
        )}
        <div
          className="absolute right-1.5 top-1.5 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7"
                aria-label={tCommon('actions')}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>
                <Eye className="mr-2 h-4 w-4" />
                {tCommon('view')}
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {tCommon('edit')}
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600" onClick={onDelete}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {tCommon('delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <p className="text-muted-foreground font-mono text-[10px] uppercase tracking-wider">
          {product.fullCode}
        </p>
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-medium leading-tight">
          {product.name}
        </h3>
        <p className="text-muted-foreground truncate text-xs">
          {product.model.brand.name} · {product.model.name}
        </p>
        <div className="mt-auto flex items-center justify-between pt-1.5">
          <span className={`px-1.5 py-0.5 text-[10px] font-medium ${stockStatusClass}`}>
            {stockStatusLabel}
          </span>
          <div className="text-right text-sm">
            {product.priceOnRequest ? (
              <span className="text-muted-foreground text-xs">{priceOnRequestLabel}</span>
            ) : product.price ? (
              <span className="font-semibold tabular-nums">
                {product.currency} {Number(product.price).toLocaleString()}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
